import { useLocale, useTranslations } from 'next-intl';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { ElasticVehicle, ElasticVehicleAccessory } from '@//app/api/search/vehicles/[id]';
import { Button } from '@//components/Button';
import { PromoCard } from '@//components/PromoCard';
import { useLegacyTrackAccessoryExposure } from '@//data/tracking/useLegacyTrackAccessoryExposure';
import { useLegacyTrackAccessoryInformationClick } from '@//data/tracking/useLegacyTrackAccessoryInformationClick';
import { useMarketMetadata } from '@//providers/MarketSettingsProvider';
import { formatCurrency } from '@//utils/currency';
import { urlToCfit } from '@//utils/image/urlToCfit';

interface AccessoryItemProps {
  id: string;
  accessory: ElasticVehicleAccessory;
  vehicle: ElasticVehicle;
  onOpenModal: (accessory: ElasticVehicleAccessory) => void;
}

const AccessoryItem = ({ id, accessory, vehicle, onOpenModal }: AccessoryItemProps) => {
  const t = useTranslations('pages.item.sellerSection.accessories');
  const locale = useLocale();
  const { currency } = useMarketMetadata();
  const { trackLegacyAccessoryExposure } = useLegacyTrackAccessoryExposure();
  const { trackLegacyAccessoryInformationClick } = useLegacyTrackAccessoryInformationClick();

  const cardRef = useRef<HTMLDivElement>(null);
  const hasTrackedExposure = useRef(false);

  const mediaUrl = useMemo(
    () => accessory.media?.slice().sort((a, b) => a.sortOrder - b.sortOrder)?.[0]?.url,
    [accessory.media],
  );

  const image = mediaUrl ? urlToCfit(mediaUrl, { width: 600, alt: accessory.name }) : undefined;

  const currentPrice = formatCurrency(accessory.salePrice ?? accessory.price, {
    locale,
    currency,
  });
  const oldPrice =
    accessory.salePrice && accessory.price
      ? formatCurrency(accessory.price, { locale, currency })
      : undefined;

  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;

      if (entry.isIntersecting && !hasTrackedExposure.current) {
        trackLegacyAccessoryExposure({
          vehicle,
          information: {
            context: 'productOverview',
            id: accessory.id,
            label: accessory.name,
          },
        });
        hasTrackedExposure.current = true;
      }
    },
    [trackLegacyAccessoryExposure, vehicle, accessory.id, accessory.name],
  );

  useEffect(() => {
    const cardElement = cardRef.current;
    if (!cardElement) return;

    const observer = new IntersectionObserver(handleIntersection, {
      threshold: 0.5,
      rootMargin: '0px',
    });

    observer.observe(cardElement);

    return () => {
      observer.disconnect();
    };
  }, [handleIntersection]);

  const handleReadMoreClick = useCallback(() => {
    trackLegacyAccessoryInformationClick({
      vehicle,
      information: {
        context: 'productOverview',
        id: accessory.id,
        label: accessory.name,
      },
    });
    onOpenModal(accessory);
  }, [trackLegacyAccessoryInformationClick, vehicle, accessory, onOpenModal]);

  return (
    <div ref={cardRef}>
      <PromoCard
        id={id}
        image={
          image && {
            src: image.src,
            srcSet: image.srcSet,
            alt: accessory.name,
          }
        }
        heading={accessory.name}
        headingLevel={4}
        price={{ current: currentPrice, old: oldPrice }}
        action={
          <Button
            size="sm"
            variant="secondary"
            onClick={handleReadMoreClick}
            title={t('readMoreLink.title', { name: accessory.name })}
          >
            {t('readMoreLink.label')}
          </Button>
        }
        testId={id}
      />
    </div>
  );
};
export default AccessoryItem;
